import { useCallback, useRef, useState } from 'react';

/**
 * Garantit qu'une seule exécution de `fn` est en vol à la fois : les appels
 * effectués pendant une exécution en cours sont ignorés (no-op).
 *
 * À utiliser pour les writes API déclenchés par l'UI (boutons, sélections de
 * liste) : une garde `isSubmitting` en state ne suffit pas, car deux appels
 * rapprochés passent tous les deux avant le re-render (setState est asynchrone).
 * Résultat côté back : des PUT concurrents sur la même entité et des conflits
 * de concurrence optimiste (AbpDbConcurrencyException / HTTP 409).
 *
 * `fn` doit être mémoïsé (useCallback) par l'appelant. Les exceptions de `fn`
 * sont propagées à l'appelant de `run` ; `isInFlight` est relâché dans tous
 * les cas.
 */
export const useSingleFlight = <Args extends unknown[], R>(
  fn: (...args: Args) => Promise<R>,
): { run: (...args: Args) => Promise<R | undefined>; isInFlight: boolean } => {
  const inFlightRef = useRef(false);
  const [isInFlight, setIsInFlight] = useState(false);

  const run = useCallback(async (...args: Args): Promise<R | undefined> => {
    if (inFlightRef.current) return undefined;
    inFlightRef.current = true;
    setIsInFlight(true);
    try {
      return await fn(...args);
    } finally {
      inFlightRef.current = false;
      setIsInFlight(false);
    }
  }, [fn]);

  return { run, isInFlight };
};
